// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  fetchDataPointsReport,
  fetchTeacherSubjectsWithStudents,
  fetchAdminStudents,
  fetchDataPointsReportClasses,
  updateDataPoint,
  deleteDataPoint,
  fetchEvidenceFile,
} from '../services/api';
import StudentNameOrderToggle from '../components/StudentNameOrderToggle';
import { getStudentSortKey, useStudentNameOrder } from '../utils/studentSort';
import { COLOR_SCORE_PRESETS, GRADING_COLORS, GRADING_STYLES, colorFromScore } from '../utils/grading';
import '../components/DataSheet.css';
import AdditionalSupportIndicator, { ADDITIONAL_SUPPORT_LABEL } from '../components/AdditionalSupportIndicator';
import ClassStandardsMatrix, { buildClassMatrix } from '../components/ClassStandardsMatrix';
import { fetchAssignments } from '../services/assignments';

const COLOR_STYLES = GRADING_STYLES;

const STATUS_SCORE_PRESETS = COLOR_SCORE_PRESETS;

const GRADE_ORDER = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

function compareGrades(left, right) {
  const rank = (grade) => {
    const index = GRADE_ORDER.indexOf(String(grade).toUpperCase());
    return index === -1 ? GRADE_ORDER.length : index;
  };
  return rank(left) - rank(right) || String(left).localeCompare(String(right), undefined, { numeric: true });
}

// Graded assignments/assessments publish data points titled after the task; submission (timeliness)
// points carry a " - Submission" suffix and belong to the same assignment.
export function assignmentName(row) {
  const type = String(row?.data_type || '').toLowerCase();
  const title = String(row?.title || '').trim();
  if (!title || (type !== 'assignment' && type !== 'assessment')) return '';
  return title.replace(/\s+-\s+Submission(?:\s+\(.*\))?$/, '');
}

// Graded learning tasks publish points titled "<task title>" plus "<task title> - Submission (...)" for timeliness.
function baseTaskTitle(title) {
  return String(title || '').trim().replace(/\s+-\s+Submission(?:\s+\(.*\))?$/, '').toLowerCase();
}

function normalizeGrade(value) {
  return String(value || '').trim().toUpperCase().replace(/^GRADE\s+/, '').replace(/^KINDERGARTEN$/, 'K');
}

/**
 * Index published learning tasks so a data point maps to the task that produced it: same title and a student assigned
 * to that task. Points from assignments that aren't on the server (older or manual entries) fall back to their title.
 */
export function buildAssignmentIndex(tasks) {
  const list = (tasks || [])
    .filter((task) => task && task.id && task.status !== 'draft' && String(task.title || '').trim())
    .map((task) => ({
      key: `task:${task.id}`,
      title: String(task.title).trim(),
      classId: task.classId == null ? '' : String(task.classId),
      className: task.className || '',
      subject: task.subject || '',
      grade: task.grade || '',
      status: task.status,
      dueDate: task.dueDate || '',
      standards: task.standards || [],
      students: (task.students || []).map((st) => ({ student_id: String(st.studentId), label: st.name || `Student ${st.studentId}` })),
    }));
  list.forEach((task) => { task.studentIds = new Set(task.students.map((st) => st.student_id)); });
  const byTitle = new Map();
  list.forEach((task) => {
    const norm = task.title.toLowerCase();
    if (!byTitle.has(norm)) byTitle.set(norm, []);
    byTitle.get(norm).push(task);
  });
  const keysOf = (row) => {
    const matches = (byTitle.get(baseTaskTitle(row?.title)) || []).filter((task) => task.studentIds.has(String(row.student_id)));
    if (matches.length) return matches.map((task) => task.key);
    const name = assignmentName(row);
    return name ? [`title:${name}`] : [];
  };
  return { list, byKey: new Map(list.map((task) => [task.key, task])), keysOf };
}

function splitNames(value) {
  return String(value || '').split(',').map((name) => name.trim()).filter(Boolean);
}

function deriveColorFromScore(scoreValue, explicitColor) {
  const derived = colorFromScore(scoreValue);
  if (derived) return derived;
  if (GRADING_COLORS.includes(explicitColor)) {
    return explicitColor;
  }
  return colorFromScore(scoreValue);
}

function pickFirstNonEmpty(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const asString = String(value).trim();
    if (asString) return asString;
  }
  return '';
}

function inferSubjectFromStandardCode(standardCode) {
  const code = String(standardCode || '').trim();
  if (!code) return '';

  // Lowercase suffixes (e.g. "3.LS.1.A.d") are expectation letters, not domain codes.
  const tokens = code.split(/[^A-Za-z0-9]+/).filter(Boolean);
  const domainTokenSet = new Set(tokens.filter((token) => /^[A-Z]+$/.test(token)));

  const hasAny = (candidates) => candidates.some((candidate) => domainTokenSet.has(candidate));

  if (hasAny(['RF', 'RL', 'RI', 'W', 'SL', 'L'])) return 'Literacy';
  if (hasAny(['NO', 'AR', 'GM', 'D', 'NS', 'NBT', 'NF', 'RA', 'EEI', 'RP', 'F', 'DS', 'DSP'])) return 'Mathematics';
  if (hasAny(['ESS', 'LS', 'PS', 'ETS'])) return 'Science';
  if (hasAny(['SED', 'AD', 'CD', 'SEL', 'SAS', 'GC'])) return 'Success Skills';
  if (hasAny(['H', 'GS', 'G', 'E', 'EG', 'PC', 'C', 'TS'])) return 'Social Studies';

  return '';
}

function inferSubjectFromDescription(description) {
  const text = String(description || '').toLowerCase();
  if (!text) return '';

  if (/(phonics|reading|writing|language|literature|informational|fluency|comprehension)/.test(text)) return 'Literacy';
  if (/(number|algebra|geometry|measurement|fraction|equation|statistics|probability|math)/.test(text)) return 'Mathematics';
  if (/(science|engineering|matter|energy|earth|space|life science|physical science)/.test(text)) return 'Science';
  if (/(history|geography|economics|civic|government|social studies|inquiry)/.test(text)) return 'Social Studies';
  if (/(social emotional|career development|academic development|success skills)/.test(text)) return 'Success Skills';

  return '';
}

function normalizeReportRow(row) {
  const gradeLevel = pickFirstNonEmpty(
    row?.grade_level,
    row?.grade,
    row?.student_grade,
    row?.current_grade
  );

  const subjectName = pickFirstNonEmpty(
    row?.subject_name,
    row?.subject,
    row?.course_title,
    row?.subject_title,
    inferSubjectFromStandardCode(row?.standard_code),
    inferSubjectFromDescription(row?.standard_description)
  );

  const teacherName = pickFirstNonEmpty(
    row?.teacher_name,
    row?.teacher,
    row?.teacher_full_name,
    row?.entered_by
  );

  return {
    ...row,
    grade_level: gradeLevel,
    subject_name: subjectName,
    teacher_name: teacherName,
    color: deriveColorFromScore(row?.score, row?.color),
  };
}

function ColorBadge({ color }: any) {
  if (!color) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
  const s = COLOR_STYLES[color];
  if (!s) return <span>{color}</span>;
  return (
    <span className="data-sheet-status-badge" style={{ background: s.bg, color: s.text }}>
      {s.label}
    </span>
  );
}

function StandardDescription({ row }: any) {
  const description = row.standard_description
    || row.cluster_expectation_description
    || row.cluster_description;

  return <span>{description || '—'}</span>;
}

export default function DataPointsReport({ role }: any) {
  const { user } = useAuth();
  const effectiveRole = role || (user && user.role) || 'unknown';
  const isAdmin = effectiveRole === 'admin' || effectiveRole === 'school_admin' || effectiveRole === 'district_admin';
  const managedSchoolIds = new Set((user?.managed_schools || []).map(school => school.school_id).filter(id => id != null).map(String));
  const canFilterSchools = isAdmin && managedSchoolIds.size > 1;
  const canManageRows = effectiveRole === 'teacher' || effectiveRole === 'special_services_teacher';

  useEffect(() => {
    console.log('[DEBUG] DataPointsReport MOUNTED');
    return () => console.log('[DEBUG] DataPointsReport UNMOUNTED');
  }, []);

  const [viewMode, setViewMode] = useState('student'); // 'student' | 'class'

  const [students, setStudents] = useState<any>([]); // flat list of all students
  const [classes, setClasses] = useState<any>([]);   // active classes visible to the user

  const [selectedStudentId, setSelectedStudentId] = useState<any>('');
  const [nameOrder] = useStudentNameOrder();

  const [allRows, setAllRows] = useState<any>([]);
  const [rows, setRows] = useState<any>([]);
  // Filters
  const [gradeFilter, setGradeFilter] = useState<any>('');
  const [subjectFilter, setSubjectFilter] = useState<any>('');
  const [statusFilter, setStatusFilter] = useState<any>('');
  const [teacherFilter, setTeacherFilter] = useState<any>('');
  const [schoolFilter, setSchoolFilter] = useState<any>('');
  useEffect(() => {
    if (!canFilterSchools) setSchoolFilter('');
  }, [canFilterSchools]);
  const [classFilter, setClassFilter] = useState<any>('');
  const [assignmentFilter, setAssignmentFilter] = useState<any>(''); // 'task:<id>' or 'title:<name>'
  const [learningTasks, setLearningTasks] = useState<any>([]);
  const [tasksLoaded, setTasksLoaded] = useState<any>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [linkedTaskId, setLinkedTaskId] = useState(() => searchParams.get('assignment') || '');
  const [linkNotice, setLinkNotice] = useState<any>('');
  const [loading, setLoading] = useState<any>(false);
  const [savingId, setSavingId] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<any>(null);
  const [editingId, setEditingId] = useState<any>(null);
  const [editingForm, setEditingForm] = useState<any>(null);
  const [viewingEvidenceId, setViewingEvidenceId] = useState<any>(null);
  const [selectedCellKey, setSelectedCellKey] = useState<any>(null);

  const [error, setError] = useState<any>(null);
  const [actionError, setActionError] = useState<any>('');
  const [actionErrorId, setActionErrorId] = useState<any>(null);

  const normalizeRows = (data) => (Array.isArray(data) ? data.map(normalizeReportRow) : []);

  // Load students/classes for dropdowns
  useEffect(() => {
    async function loadFilters() {
      try {
        if (isAdmin) {
          // Fetch real students for admin
          const data = await fetchAdminStudents();
          const allStudents = (data.students || []).map(st => ({
            student_id: st.student_id,
            first_name: st.first_name,
            last_name: st.last_name,
            label: `${st.first_name || ''} ${st.last_name || ''}`.trim() || `Student ${st.student_id}`,
          }));
          setStudents(allStudents.sort((a, b) => a.label.localeCompare(b.label)));
        } else {
          // Fallback to previous logic for teachers
          const data = await fetchTeacherSubjectsWithStudents();
          const studentMap = {};
          (data.subjects || data || []).forEach(subject => {
            (subject.classes || []).forEach(cls => {
              (cls.students || []).forEach(st => {
                studentMap[st.student_id] = {
                  student_id: st.student_id,
                  first_name: st.first_name,
                  last_name: st.last_name,
                  label: `${st.first_name || ''} ${st.last_name || ''}`.trim() || `Student ${st.student_id}`,
                };
              });
            });
          });
          setStudents(Object.values(studentMap).sort((a, b) => a.label.localeCompare(b.label)));
        }
      } catch {
        // non-fatal
      }
    }
    async function loadClasses() {
      try {
        // One list for the Class dropdown: every active class the user teaches (admins: whole school).
        const data = await fetchDataPointsReportClasses();
        setClasses((Array.isArray(data) ? data : []).map((cls) => ({
          class_id: String(cls.class_id),
          subject_name: cls.subject_name || '',
          label: cls.subject_name ? `${cls.class_name} (${cls.subject_name})` : cls.class_name,
        })));
      } catch {
        // non-fatal
      }
    }
    async function loadTasks() {
      try {
        // Learning tasks the user can see (teachers: their classes; admins: the school) feed the Assignment dropdown.
        const data = await fetchAssignments();
        setLearningTasks(Array.isArray(data) ? data : []);
      } catch {
        // non-fatal: the dropdown still lists assignments found in the data points
      } finally {
        setTasksLoaded(true);
      }
    }
    loadFilters();
    loadClasses();
    loadTasks();
  }, [isAdmin]);

  useEffect(() => {
    async function preloadAllRows() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchDataPointsReport({});
        const normalized = normalizeRows(data);
        setAllRows(normalized);
        setRows(normalized);
      } catch {
        setError('Failed to load data points. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    preloadAllRows();
  }, []);

  const loadRequestRef = useRef<any>(0);
  const loadRows = async ({ studentId = '' } : any = {}) => {
    const requestId = ++loadRequestRef.current;
    setError(null);
    if (!studentId) {
      setRows(allRows);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (studentId) params.studentId = studentId;
      const data = await fetchDataPointsReport(params);
      if (requestId === loadRequestRef.current) setRows(normalizeRows(data));
    } catch (e: any) {
      if (requestId === loadRequestRef.current) setError('Failed to load data points. Please try again.');
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  };

  const clearFilters = () => {
    loadRequestRef.current += 1;
    setLoading(false);
    setSelectedStudentId('');
    setGradeFilter('');
    setSubjectFilter('');
    setStatusFilter('');
    setTeacherFilter('');
    setSchoolFilter('');
    setClassFilter('');
    setAssignmentFilter('');
    setLinkNotice('');
    setRows(allRows);
    setError(null);
    setActionError('');
    setActionErrorId(null);
  };

  // Every dropdown lists only values present in the loaded rows that also match the *other* selected filters,
  // so each option leads to data. Class/school names arrive comma-joined for students in several classes.
  const assignmentIndex = React.useMemo(() => buildAssignmentIndex(learningTasks), [learningTasks]);
  const selectedTask = assignmentIndex.byKey.get(assignmentFilter) || null;

  // Opened from the LMS (/data-points-report?assignment=<id>): show that assignment's class grid.
  useEffect(() => {
    if (!linkedTaskId || !tasksLoaded || loading) return;
    const task = assignmentIndex.byKey.get(`task:${linkedTaskId}`);
    if (task) {
      setViewMode('class');
      setSelectedStudentId('');
      setClassFilter(task.classId);
      const point = allRows.find((row) => assignmentIndex.keysOf(row).includes(task.key));
      const taskClass = classes.find((cls) => cls.class_id === task.classId);
      setSubjectFilter(point?.subject_name || taskClass?.subject_name || task.subject);
      setAssignmentFilter(task.key);
      setSelectedCellKey(null);
      setLinkNotice('');
    } else {
      setLinkNotice('That assignment is not available in View Data. Drafts are not shown until they are published to students.');
    }
    setLinkedTaskId('');
  }, [linkedTaskId, tasksLoaded, loading, assignmentIndex, allRows, classes]);

  // Keep the link in the address bar in step with the selected learning task so it can be bookmarked or shared.
  useEffect(() => {
    if (linkedTaskId) return;
    const current = searchParams.get('assignment') || '';
    const wanted = selectedTask ? assignmentFilter.replace(/^task:/, '') : '';
    if (current === wanted) return;
    const next = new URLSearchParams(searchParams);
    if (wanted) next.set('assignment', wanted); else next.delete('assignment');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentFilter, selectedTask, linkedTaskId]);
  const facetMatchers = {
    grade: (r) => !gradeFilter || r.grade_level === gradeFilter,
    subject: (r) => !subjectFilter || r.subject_name === subjectFilter,
    status: (r) => !statusFilter || r.color === statusFilter,
    teacher: (r) => !teacherFilter || r.teacher_name === teacherFilter,
    school: (r) => !schoolFilter || splitNames(r.school_name).includes(schoolFilter),
    className: (r) => !classFilter || (r.class_ids || []).map(String).includes(classFilter),
    assignment: (r) => !assignmentFilter || assignmentIndex.keysOf(r).includes(assignmentFilter),
    student: (r) => viewMode !== 'student' || !selectedStudentId || String(r.student_id) === String(selectedStudentId),
  };
  const rowsExcept = (facet) => rows.filter((r) => Object.entries(facetMatchers).every(([key, match]) => key === facet || match(r)));
  const facetValues = (facet, pick) => Array.from(new Set(rowsExcept(facet).flatMap(pick).filter(Boolean)));

  const grades = facetValues('grade', (r) => [r.grade_level]).sort(compareGrades);
  const subjects = facetValues('subject', (r) => [r.subject_name]).sort();
  const teachers = facetValues('teacher', (r) => [r.teacher_name]).sort();
  const schools = facetValues('school', (r) => splitNames(r.school_name)).sort();
  const availableStatuses = new Set(facetValues('status', (r) => [r.color]));
  const statuses = ['green', 'lightgreen', 'yellow', 'red', 'gray'].filter((s) => availableStatuses.has(s));
  // A learning task is offered when it belongs to the selected class/student and either has graded points under the
  // other selections or (not graded yet) matches the selected subject and grade.
  const taskInScope = (task) => (!classFilter || task.classId === classFilter)
    && (viewMode !== 'student' || !selectedStudentId || task.studentIds.has(String(selectedStudentId)));
  const taskSubject = (task) => classes.find((c) => c.class_id === task.classId)?.subject_name || task.subject;
  const ungradedTaskMatches = (task) => !statusFilter && !teacherFilter && !schoolFilter
    && (!subjectFilter || String(taskSubject(task)).toLowerCase() === subjectFilter.toLowerCase())
    && (!gradeFilter || normalizeGrade(task.grade) === normalizeGrade(gradeFilter));
  const gradedAssignmentKeys = new Set(facetValues('assignment', (r) => assignmentIndex.keysOf(r)));
  const assignmentKeys = new Set([
    ...[...gradedAssignmentKeys].filter((key) => !assignmentIndex.byKey.has(key) || taskInScope(assignmentIndex.byKey.get(key))),
    ...assignmentIndex.list.filter((task) => taskInScope(task) && ungradedTaskMatches(task)
      && !rows.some((r) => assignmentIndex.keysOf(r).includes(task.key))).map((task) => task.key),
  ]);
  const baseAssignmentLabel = (key) => assignmentIndex.byKey.get(key)?.title || key.replace(/^title:/, '');
  const titleCounts = {};
  assignmentKeys.forEach((key) => { const t = baseAssignmentLabel(key); titleCounts[t] = (titleCounts[t] || 0) + 1; });
  const assignmentLabel = (key) => {
    const task = assignmentIndex.byKey.get(key);
    const label = baseAssignmentLabel(key);
    if (task && titleCounts[label] > 1 && task.className) return `${label} (${task.className})`;
    return label;
  };
  const assignments = [...assignmentKeys]
    .map((key) => ({ key, label: assignmentLabel(key) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const hasRowFilters = Boolean(gradeFilter || subjectFilter || statusFilter || teacherFilter || schoolFilter || classFilter || assignmentFilter);
  // Student choices come from all rows (the loaded rows narrow to one student once one is picked).
  const studentIdsMatchingFilters = new Set(allRows
    .filter((r) => Object.entries(facetMatchers).every(([key, match]) => key === 'student' || match(r)))
    .map((r) => String(r.student_id)));
  // Students assigned to the selected learning task stay selectable even before they're graded.
  if (selectedTask) selectedTask.studentIds.forEach((id) => studentIdsMatchingFilters.add(id));
  const studentOptions = Array.from(new Map([
    ...students.map(student => [String(student.student_id), student]),
    ...allRows
      .filter(row => row.student_id !== null && row.student_id !== undefined)
      .map(row => [String(row.student_id), { student_id: row.student_id, label: row.student_name || `Student ${row.student_id}` }]),
  ]).values())
    .filter((s) => !hasRowFilters || studentIdsMatchingFilters.has(String(s.student_id)) || String(s.student_id) === String(selectedStudentId))
    .sort((a, b) => getStudentSortKey(a, nameOrder).localeCompare(getStudentSortKey(b, nameOrder)));

  const facetKey = [grades, subjects, teachers, schools, statuses, assignments.map((a) => a.key)].map((list) => list.join('|')).join('#');
  useEffect(() => {
    if (loading || linkedTaskId) return;
    // Drop selections the other filters (or a newly loaded class/student) have made impossible.
    if (gradeFilter && !grades.includes(gradeFilter)) setGradeFilter('');
    if (statusFilter && !statuses.includes(statusFilter)) setStatusFilter('');
    if (teacherFilter && !teachers.includes(teacherFilter)) setTeacherFilter('');
    if (schoolFilter && !schools.includes(schoolFilter)) setSchoolFilter('');
    if (assignmentFilter && !assignmentKeys.has(assignmentFilter)) setAssignmentFilter('');
    // The standards grid shows one subject at a time.
    if (!subjects.includes(subjectFilter)) setSubjectFilter(subjects[0] || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, linkedTaskId, viewMode, facetKey, gradeFilter, subjectFilter, statusFilter, teacherFilter, schoolFilter, classFilter, assignmentFilter]);

  const filteredRows = rows.filter((r) => Object.values(facetMatchers).every((match) => match(r)));

  const groupedByStudent = {};
  filteredRows.forEach(r => {
    if (!groupedByStudent[r.student_id]) groupedByStudent[r.student_id] = { name: r.student_name, grade: r.student_grade_level || r.grade_level, rows: [] };
    groupedByStudent[r.student_id].rows.push(r);
  });

  // Everyone assigned the selected learning task gets a row (blank until graded); for assignments only known from
  // data points, the selected class's students do.
  const studentNameById = new Map(rows.map((r) => [String(r.student_id), r.student_name]));
  const assignmentRoster = selectedTask
    ? selectedTask.students
      .filter((st) => viewMode !== 'student' || !selectedStudentId || st.student_id === String(selectedStudentId))
      .map((st) => ({ student_id: st.student_id, label: studentNameById.get(st.student_id) || st.label }))
    : assignmentFilter && classFilter
    ? Array.from(new Map(rows
      .filter((r) => (r.class_ids || []).map(String).includes(classFilter))
      .filter((r) => viewMode !== 'student' || !selectedStudentId || String(r.student_id) === String(selectedStudentId))
      .map((r) => [String(r.student_id), { student_id: r.student_id, label: r.student_name || `Student ${r.student_id}` }])).values())
    : [];
  const classMatrix = buildClassMatrix(filteredRows, assignmentRoster);
  if (selectedTask && !classMatrix.subjects.length) {
    const standards = selectedTask.standards.filter((standard) => standard.mlsCode);
    if (standards.length) classMatrix.subjects = [{
      subject: subjectFilter || taskSubject(selectedTask) || 'Other',
      standards: standards.map((standard) => ({
        code: standard.mlsCode,
        description: standard.friendlyTitle || standard.description || '',
      })),
    }];
  }
  const showMatrix = filteredRows.length > 0 || (assignmentRoster.length > 0 && classMatrix.subjects.length > 0);
  const selectedCellRows = (selectedCellKey && classMatrix.cells.get(selectedCellKey)) || [];
  // A cell emptied by deleting/filtering closes its details panel.
  const activeCellKey = selectedCellRows.length ? selectedCellKey : null;
  const selectedCellStudent = activeCellKey
    ? classMatrix.students.find((student) => student.id === String(selectedCellRows[0].student_id))
    : null;
  const selectedCellCode = activeCellKey ? (selectedCellRows[0].standard_code || 'No standard') : '';

  const beginEditRow = (row) => {
    setActionError('');
    setActionErrorId(null);
    setEditingId(row.id);
    setEditingForm({
      standard_code: row.standard_code || null,
      student_id: row.student_id,
      title: row.title || '',
      score: row.score ?? '',
      notes: row.notes || '',
      notes_visibility: row.notes_visibility || 'private',
      accommodations_applied: row.accommodations_applied === true,
      date: row.date || '',
      evidence: row.evidence || null,
      color: deriveColorFromScore(row.score, row.color),
    });
  };

  const cancelEditRow = () => {
    setEditingId(null);
    setEditingForm(null);
    setActionError('');
    setActionErrorId(null);
  };

  const saveEditedRow = async () => {
    if (!editingId || !editingForm) return;
    setSavingId(editingId);
    setActionError('');
    setActionErrorId(null);
    try {
      const scoreValue = editingForm.score === '' ? null : Number(editingForm.score);
      const payload = {
        standard_code: editingForm.standard_code,
        student_id: Number(editingForm.student_id),
        title: editingForm.title?.trim() ? editingForm.title.trim() : null,
        score: Number.isFinite(scoreValue) ? Math.round(scoreValue) : null,
        notes: editingForm.notes?.trim() ? editingForm.notes : null,
        notes_visibility: editingForm.notes_visibility,
        accommodations_applied: editingForm.accommodations_applied,
        date: editingForm.date || null,
        evidence: editingForm.evidence || null,
        color: deriveColorFromScore(scoreValue, editingForm.color),
      };

      await updateDataPoint(editingId, payload);
      setRows((prev) => prev.map((row) => (
        row.id === editingId
          ? {
              ...row,
              title: payload.title,
              score: payload.score,
              notes: payload.notes,
              notes_visibility: payload.notes_visibility,
              accommodations_applied: payload.accommodations_applied,
              date: payload.date,
              evidence: payload.evidence,
              color: payload.color,
            }
          : row
      )));
      cancelEditRow();
    } catch (e: any) {
      setActionError(e.message || 'Failed to update this data point.');
      setActionErrorId(editingId);
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (row) => {
    if (!row?.id) return;
    if (!window.confirm('Delete this data point? This cannot be undone.')) return;
    setDeletingId(row.id);
    setActionError('');
    setActionErrorId(null);
    try {
      await deleteDataPoint(row.id);
      setRows((prev) => prev.filter((item) => item.id !== row.id));
      if (editingId === row.id) {
        cancelEditRow();
      }
    } catch (e: any) {
      setActionError(e.message || 'Failed to delete this data point.');
      setActionErrorId(row.id);
    } finally {
      setDeletingId(null);
    }
  };

  const viewEvidence = async (row) => {
    if (!row?.evidence) return;
    setViewingEvidenceId(row.id);
    setActionError('');
    setActionErrorId(null);
    try {
      const blob = await fetchEvidenceFile(row.evidence);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      setActionError(e.message || 'Could not open the attached evidence file.');
    } finally {
      setViewingEvidenceId(null);
    }
  };

  const renderDataTable = (tableRows) => (
    <div className="data-sheet-container">
      <table className="data-sheet" role="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr role="row" style={{ background: '#eff6ff' }}>
            <th style={thStyle}>Standard Code</th>
            <th style={thStyle}>Standard Grade</th>
            <th style={thStyle}>Data Point Title</th>
            <th style={thStyle}>Description</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Score</th>
            <th style={thStyle}>Date</th>
            {isAdmin && <th style={thStyle}>Author</th>}
            <th style={thStyle}>Notes</th>
            <th style={thStyle}>Evidence</th>
            <th style={thStyle}>Notes &amp; Evidence Visibility</th>
            {canManageRows && <th style={thStyle}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {tableRows.map((r, i) => {
            const isEditingRow = editingId === r.id;
            return (
              <tr key={r.id} role="row" className={(isEditingRow ? editingForm.accommodations_applied : r.accommodations_applied) ? 'ae-supported-data-point' : undefined} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                <td role="cell" data-label="Standard Code" style={tdStyle}><code style={{ fontSize: 11, color: '#1d4ed8' }}>{r.standard_code || '—'}</code></td>
                <td role="cell" data-label="Standard Grade" style={tdStyle}>{r.grade_level || '—'}</td>
                <td role="cell" data-label="Data Point Title" style={{ ...tdStyle, fontWeight: 700, color: '#334155' }}>
                  {isEditingRow ? (
                    <input
                      type="text"
                      value={editingForm?.title || ''}
                      onChange={(e) => setEditingForm((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="Data point title"
                      style={{ width: '100%', minWidth: 150, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--ae-border)', fontSize: 12, boxSizing: 'border-box' }}
                    />
                  ) : (
                    <div className="ae-data-point-header">
                      <span>{r.title || 'Untitled data point'}</span>
                      <AdditionalSupportIndicator applied={r.accommodations_applied} />
                    </div>
                  )}
                  {isEditingRow && (
                    <label className="ae-support-checkbox">
                      <input type="checkbox" checked={editingForm.accommodations_applied}
                        onChange={(event) => setEditingForm((previous) => ({ ...previous, accommodations_applied: event.target.checked }))} />
                      <span>{ADDITIONAL_SUPPORT_LABEL}</span>
                    </label>
                  )}
                </td>
                <td role="cell" data-label="Description" style={{ ...tdStyle, maxWidth: 360, color: '#374151' }}><StandardDescription row={r} /></td>
                <td role="cell" data-label="Status" style={tdStyle}>
                  {isEditingRow ? (
                    <select
                      aria-label="Data point status"
                      value={editingForm?.color || ''}
                      onChange={(e) => {
                        const nextColor = e.target.value || null;
                        setEditingForm((prev) => ({
                          ...prev,
                          color: nextColor,
                          score: nextColor ? STATUS_SCORE_PRESETS[nextColor] : prev?.score,
                        }));
                      }}
                      style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--ae-border)', fontSize: 12, minWidth: 130 }}
                    >
                      <option value="">Auto from score</option>
                      <option value="green">Proficient</option>
                      <option value="lightgreen">On Track</option>
                      <option value="yellow">Learning Zone</option>
                      <option value="red">Not Yet</option>
                    </select>
                  ) : (
                    <ColorBadge color={r.color} />
                  )}
                </td>
                <td role="cell" data-label="Score" style={{ ...tdStyle, textAlign: 'center' }}>
                  {isEditingRow ? (
                    <input
                      type="number"
                      value={editingForm?.score ?? ''}
                      onChange={(e) => {
                        const nextScore = e.target.value;
                        // A corrected score re-derives the status; the status dropdown can still override it.
                        setEditingForm((prev) => ({
                          ...prev,
                          score: nextScore,
                          color: nextScore === '' ? prev?.color : deriveColorFromScore(nextScore, null),
                        }));
                      }}
                      style={{ width: 88, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--ae-border)', fontSize: 12 }}
                    />
                  ) : (
                    r.score ?? '—'
                  )}
                </td>
                <td role="cell" data-label="Date" style={tdStyle}>
                  {isEditingRow ? (
                    <input
                      type="date"
                      value={editingForm?.date || ''}
                      onChange={(e) => setEditingForm((prev) => ({ ...prev, date: e.target.value }))}
                      style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--ae-border)', fontSize: 12 }}
                    />
                  ) : (
                    r.date || '—'
                  )}
                </td>
                {isAdmin && <td role="cell" data-label="Author" style={tdStyle}>{r.teacher_name || '—'}</td>}
                <td role="cell" data-label="Notes" style={{ ...tdStyle, maxWidth: 220, color: '#6b7280', fontStyle: r.notes ? 'normal' : 'italic' }}>
                  {isEditingRow ? (
                    <textarea
                      value={editingForm?.notes || ''}
                      onChange={(e) => setEditingForm((prev) => ({ ...prev, notes: e.target.value }))}
                      rows={2}
                      style={{ width: '100%', minWidth: 180, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--ae-border)', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  ) : (
                    r.notes || 'No notes'
                  )}
                </td>
                <td role="cell" data-label="Evidence" style={{ ...tdStyle, textAlign: 'center' }}>
                  {r.evidence ? (
                    <button
                      type="button"
                      onClick={() => viewEvidence(r)}
                      disabled={viewingEvidenceId === r.id}
                      style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid var(--ae-border)', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: 12, cursor: viewingEvidenceId === r.id ? 'default' : 'pointer' }}
                    >
                      {viewingEvidenceId === r.id ? 'Opening…' : 'View'}
                    </button>
                  ) : (
                    <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>None</span>
                  )}
                </td>
                <td role="cell" data-label="Notes & Evidence Visibility" style={tdStyle}>
                  {isEditingRow ? (
                    <select
                      aria-label="Notes and evidence visibility"
                      value={editingForm.notes_visibility}
                      onChange={(event) => setEditingForm((prev) => ({ ...prev, notes_visibility: event.target.value }))}
                      style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--ae-border)', fontSize: 12 }}
                    >
                      <option value="private">Private (staff only)</option>
                      <option value="shared">Shared (families)</option>
                    </select>
                  ) : (
                    <span style={{ fontWeight: 600 }}>
                      {r.notes_visibility === 'shared' ? 'Shared (families)' : 'Private (staff only)'}
                    </span>
                  )}
                </td>
                {canManageRows && (
                  <td role="cell" data-label="Actions" style={tdStyle}>
                    {r.can_edit === false ? (
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ae-text-muted)', whiteSpace: 'normal' }} title="Only the teacher who entered this data point can edit it.">
                        View only{r.teacher_name ? ` · ${r.teacher_name}` : ''}
                      </span>
                    ) : isEditingRow ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={saveEditedRow}
                          disabled={savingId === r.id}
                          style={{ padding: '6px 10px', borderRadius: 8, border: 'none', background: '#1e3a8a', color: '#fff', fontWeight: 700, cursor: savingId === r.id ? 'default' : 'pointer', opacity: savingId === r.id ? 0.75 : 1 }}
                        >
                          {savingId === r.id ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditRow}
                          disabled={savingId === r.id}
                          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 700, cursor: savingId === r.id ? 'default' : 'pointer' }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => beginEditRow(r)}
                          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteRow(r)}
                          disabled={deletingId === r.id}
                          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #fecdd3', background: '#fff1f2', color: '#b91c1c', fontWeight: 700, cursor: deletingId === r.id ? 'default' : 'pointer', opacity: deletingId === r.id ? 0.75 : 1 }}
                        >
                          {deletingId === r.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    )}
                    {actionErrorId === r.id && (
                      <div role="alert" style={{ maxWidth: 190, marginTop: 6, color: '#b91c1c', whiteSpace: 'normal', fontSize: 11, fontWeight: 700 }}>
                        {actionError}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ background: 'var(--ae-surface)', borderRadius: 18, boxShadow: 'var(--ae-shadow-soft)', padding: 24, border: '1px solid var(--ae-border)' }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--ae-primary-strong)', marginBottom: 4 }}>Data Points Report</h1>
      <p style={{ color: 'var(--ae-text-muted)', marginBottom: 28, fontSize: 14 }}>
        View all assessment data points entered for students.
      </p>

      {/* View mode toggle */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--ae-border)', width: 'fit-content' }}>
        {['student', 'class'].map(mode => (
          <button key={mode} onClick={() => {
            if (mode === viewMode) return;
            setViewMode(mode);
            setSelectedCellKey(null);
            if (mode === 'class' && selectedStudentId) {
              setSelectedStudentId('');
              loadRows();
            }
          }}
            style={{
              padding: '9px 28px', fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none',
              background: viewMode === mode ? 'var(--ae-primary)' : 'white',
              color: viewMode === mode ? 'white' : 'var(--ae-primary-strong)',
            }}>
            {mode === 'student' ? 'By Student' : 'By Class'}
          </button>
        ))}
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap', background: 'var(--ae-surface-soft)', border: '1px solid var(--ae-border)', borderRadius: 12, padding: 12 }}>
        {viewMode === 'student' ? (
          <div>
            <label htmlFor="dp-filter-student" style={filterLabelStyle}>Student</label>
            <select
              id="dp-filter-student"
              value={selectedStudentId}
              onChange={e => { setSelectedStudentId(e.target.value); setSelectedCellKey(null); loadRows({ studentId: e.target.value }); }}
              style={{ ...filterSelectStyle, minWidth: 180 }}
            >
              <option value="">— All Students —</option>
              {studentOptions.map(s => (
                <option key={s.student_id} value={s.student_id}>{s.label}</option>
              ))}
            </select>
            <StudentNameOrderToggle style={{ marginLeft: 10 }} />
          </div>
        ) : null}
        <div>
          <label htmlFor="dp-filter-class" style={filterLabelStyle}>Class</label>
          <select
            id="dp-filter-class"
            value={classFilter}
            onChange={e => { setClassFilter(e.target.value); setSelectedCellKey(null); }}
            style={{ ...filterSelectStyle, minWidth: 180 }}
          >
            <option value="">— All Classes —</option>
            {classes.map(c => (
              <option key={c.class_id} value={c.class_id}>{c.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="dp-filter-assignment" style={filterLabelStyle}>Assignment</label>
          <select
            id="dp-filter-assignment"
            value={assignmentFilter}
            onChange={e => {
              const key = e.target.value;
              setAssignmentFilter(key);
              setSelectedCellKey(null);
              // Picking a learning task also selects its class so the grid lists that class's assigned students.
              const task = assignmentIndex.byKey.get(key);
              if (task && task.classId && classes.some((c) => c.class_id === task.classId)) setClassFilter(task.classId);
            }}
            style={{ ...filterSelectStyle, minWidth: 180, maxWidth: 260 }}
          >
            <option value="">— All Assignments —</option>
            {assignments.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
          {selectedTask && !isAdmin && (
            <button
              type="button"
              onClick={() => navigate(`/teacher/assignments/${encodeURIComponent(assignmentFilter.replace(/^task:/, ''))}`)}
              style={{ marginLeft: 8, padding: '4px 10px', borderRadius: 8, border: '1px solid var(--ae-border)', background: '#fff', color: 'var(--ae-primary-strong)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
            >
              Open in LMS
            </button>
          )}
        </div>

        {/* Additional Filters: options only list values with data under the other selections. */}
        <div>
          <label htmlFor="dp-filter-grade" style={filterLabelStyle}>Grade</label>
          <select id="dp-filter-grade" value={gradeFilter} onChange={e => { setGradeFilter(e.target.value); setSelectedCellKey(null); }} style={filterSelectStyle}>
            <option value="">— All Grades —</option>
            {grades.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="dp-filter-subject" style={filterLabelStyle}>Subject</label>
          <select id="dp-filter-subject" value={subjectFilter} onChange={e => { setSubjectFilter(e.target.value); setSelectedCellKey(null); }} style={filterSelectStyle}>
            {subjects.length === 0 && <option value="">— No Subjects —</option>}
            {subjects.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="dp-filter-status" style={filterLabelStyle}>Status</label>
          <select id="dp-filter-status" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setSelectedCellKey(null); }} style={filterSelectStyle}>
            <option value="">— All Statuses —</option>
            {statuses.map(s => <option key={s} value={s}>{COLOR_STYLES[s].label}</option>)}
          </select>
        </div>
        {isAdmin && (
          <div>
            <label htmlFor="dp-filter-author" style={filterLabelStyle}>Author</label>
            <select id="dp-filter-author" value={teacherFilter} onChange={e => { setTeacherFilter(e.target.value); setSelectedCellKey(null); }} style={filterSelectStyle}>
              <option value="">— All Authors —</option>
              {teachers.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="dp-filter-school" style={filterLabelStyle}>School</label>
          <select id="dp-filter-school" value={schoolFilter} disabled={!canFilterSchools}
            title={canFilterSchools ? undefined : 'School filtering is available to administrators with access to multiple schools.'}
            onChange={e => { setSchoolFilter(e.target.value); setSelectedCellKey(null); }} style={{ ...filterSelectStyle, minWidth: 150 }}>
            <option value="">— All Schools —</option>
            {schools.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {loading && (
          <span role="status" style={{ alignSelf: 'center', fontSize: 13, fontWeight: 600, color: 'var(--ae-text-muted)' }}>Loading…</span>
        )}
      </div>

      {error && <div style={{ color: '#ef4444', marginBottom: 16, fontWeight: 600 }}>{error}</div>}
      {actionError && !actionErrorId && <div style={{ color: '#ef4444', marginBottom: 16, fontWeight: 600 }}>{actionError}</div>}
        <button
          type="button"
          onClick={clearFilters}
          disabled={loading}
          style={{
            padding: '9px 18px', background: '#ffffff', color: '#334155', border: '1px solid var(--ae-border)',
            borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}
        >
          Clear Filters
        </button>

      {rows.length === 0 && !loading && !showMatrix && (
        <div style={{ color: '#9ca3af', textAlign: 'center', padding: '40px 0', fontSize: 15 }}>
          No data points found yet for the current view.
        </div>
      )}

      {linkNotice && (
        <p role="status" style={{ color: '#b45309', fontSize: 13, margin: '0 0 12px' }}>{linkNotice}</p>
      )}
      {rows.length > 0 && filteredRows.length === 0 && !loading && !showMatrix && (
        <div style={{ color: '#9ca3af', textAlign: 'center', padding: '22px 0', fontSize: 15 }}>
          {selectedTask && !rows.some((r) => assignmentIndex.keysOf(r).includes(assignmentFilter))
            ? 'No grades have been published to data points for this assignment yet. Use “Finalize & Publish Grades” on the assignment to add them.'
            : classFilter && !rows.some((r) => (r.class_ids || []).map(String).includes(classFilter))
              ? 'No data points yet for this class.'
              : 'No data points match the selected filters.'}
        </div>
      )}

      {showMatrix && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            <span>
              Showing <strong>{filteredRows.length}</strong> data point{filteredRows.length !== 1 ? 's' : ''}
              {` across ${Object.keys(groupedByStudent).length} student${Object.keys(groupedByStudent).length === 1 ? '' : 's'}`}
            </span>
          </div>

          <div>
              <div className="class-matrix-legend" aria-label="Status legend">
                {[...statuses, 'gray'].map((color) => (
                  <span key={color}>
                    <span className="class-matrix-dot" data-color={color} />
                    {COLOR_STYLES[color].label}
                  </span>
                ))}
                <span>Each dot is one data point · click a box to see details</span>
              </div>
              <ClassStandardsMatrix matrix={classMatrix} selectedCell={activeCellKey} onSelectCell={setSelectedCellKey} />
              {activeCellKey && selectedCellStudent && (
                <section className="class-matrix-details" aria-label="Selected data point details">
                  <div className="class-matrix-details-header">
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--ae-primary-strong)' }}>
                        {selectedCellStudent.name} · <code>{selectedCellCode}</code>
                      </div>
                      <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
                        <StandardDescription row={selectedCellRows[0]} />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedCellKey(null)}
                      style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 700, cursor: 'pointer' }}
                    >
                      Close
                    </button>
                  </div>
                  {renderDataTable(selectedCellRows)}
                </section>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 12,
  color: '#1e40af', borderBottom: '2px solid #c7d2fe',
};
const filterLabelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };
const filterSelectStyle = { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--ae-border)', fontSize: 14, minWidth: 120 };
const tdStyle = {
  padding: '8px 12px', verticalAlign: 'top',
};
